import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Login.css";

import elephant from "../assets/login/elephant.png";
import tuktuk from "../assets/login/tuktuk.png";
import lion from "../assets/login/lion.png";
import turtle from "../assets/login/turtle.png";
import palmtree from "../assets/login/palmtree.png";
import monkey from "../assets/login/monkey.png";
import flag from "../assets/login/flag.png";
import mango from "../assets/login/mango.png";
import crab from "../assets/login/crab.png";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

const pictureChoices = [
  { name: "Elephant", src: elephant },
  { name: "Tuk Tuk", src: tuktuk },
  { name: "Lion", src: lion },
  { name: "Turtle", src: turtle },
  { name: "Palm Tree", src: palmtree },
  { name: "Monkey", src: monkey },
  { name: "Flag", src: flag },
  { name: "Mango", src: mango },
  { name: "Crab", src: crab },
];

export default function Login() {
  const goToPage = useNavigate();

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [selectedPictures, setSelectedPictures] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState("");

  const selectedCards = useMemo(() => {
    return selectedPictures
      .map((selectedName) =>
        pictureChoices.find((picture) => picture.name === selectedName)
      )
      .filter(Boolean);
  }, [selectedPictures]);

  function handleSelect(picture) {
    setLoginError("");

    const selectedIndex = selectedPictures.indexOf(picture.name);

    // tapping a chosen picture again removes it from the pattern
    if (selectedIndex !== -1) {
      setSelectedPictures(
        selectedPictures.filter((item) => item !== picture.name)
      );
      return;
    }

    // only three pictures are allowed because that is the secret pattern length
    if (selectedPictures.length < 3) {
      setSelectedPictures([...selectedPictures, picture.name]);
    }
  }

  function handleOpenConfirm() {
    if (selectedPictures.length !== 3) {
      setLoginError("Please pick your 3 secret pictures first.");
      return;
    }

    setShowConfirmModal(true);
  }

  async function handleLogin() {
    const trimmedName = name.trim();

    if (!trimmedName || selectedPictures.length !== 3) {
      setLoginError("Please enter your name and pick your 3 pictures.");
      setShowConfirmModal(false);
      return;
    }

    setIsSubmitting(true);
    setLoginError("");

    try {
      const response = await fetch(`${API_BASE}/api/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: trimmedName,
          secret_images: selectedPictures,
        }),
      });

      const resultData = await response.json();

      if (!response.ok) {
        if (response.status === 404) {
          setLoginError("We couldn't find that explorer. Please check the name.");
        } else if (response.status === 401) {
          setLoginError("That secret picture pattern doesn't match. Try again.");
        } else {
          setLoginError(resultData.error || "Login failed. Please try again.");
        }

        setShowConfirmModal(false);
        return;
      }

      // store the current explorer so the other pages can show and save their progress
      localStorage.setItem(
        "exploreLankaUser",
        JSON.stringify({ name: trimmedName })
      );

      setShowConfirmModal(false);
      setLoginError("");
      goToPage("/loading", { state: { to: "/home" } });
    } catch (error) {
      console.error("Login error:", error);
      setLoginError("We couldn't reach ExploreLanka. Please try again.");
      setShowConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="loginPage">
      <button
        className="backBtn"
        onClick={() => goToPage("/loading", { state: { to: "/" } })}
        type="button"
      >
        ← Back
      </button>

      {step === 1 && (
        <div className="stepContainer">
          <h1 className="loginTitle">Welcome Back!</h1>

          {loginError && <div className="errorMessage">{loginError}</div>}

          <input
            className="nameInput fun"
            placeholder="Type your name here..."
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setLoginError("");
            }}
          />

          <button
            className="nextBtn"
            onClick={() => {
              if (!name.trim()) {
                setLoginError("Please enter your name first.");
                return;
              }

              setLoginError("");
              setStep(2);
            }}
            type="button"
          >
            Next →
          </button>

          <button
            className="forgotBtn"
            onClick={() => goToPage("/reset")}
            type="button"
          >
            I forgot my pattern
          </button>
        </div>
      )}

      {step === 2 && (
        <>
          <h1 className="loginTitle">Hi {name}! Enter Your Secret Pattern</h1>

          <p className="instruction">
            Pick your 3 secret pictures in the correct order
          </p>

          {loginError && <div className="errorMessage">{loginError}</div>}

          <div className="selectedPreview">
            {selectedPictures.length === 0 ? (
              <span className="selectedPlaceholder">
                No pictures chosen yet
              </span>
            ) : (
              selectedPictures.map((item, index) => (
                <span key={item} className="selectedItem">
                  {index + 1}. {item}
                </span>
              ))
            )}
          </div>

          <div className="imageGrid">
            {pictureChoices.map((picture) => {
              const selectedIndex = selectedPictures.indexOf(picture.name);
              const isSelected = selectedIndex !== -1;

              return (
                <button
                  key={picture.name}
                  className={`imageCard ${isSelected ? "selected" : ""}`}
                  onClick={() => handleSelect(picture)}
                  type="button"
                >
                  {isSelected && (
                    <div className="numberBadge">{selectedIndex + 1}</div>
                  )}

                  <img src={picture.src} alt={picture.name} />
                  <p>{picture.name}</p>
                </button>
              );
            })}
          </div>

          <button
            className="submitBtn"
            onClick={handleOpenConfirm}
            type="button"
          >
            Log In
          </button>

          <button
            className="forgotBtn"
            onClick={() => goToPage("/reset")}
            type="button"
          >
            I forgot my pattern
          </button>
        </>
      )}

      {showConfirmModal && (
        <div
          className="modalOverlay"
          onClick={() => {
            if (!isSubmitting) {
              setShowConfirmModal(false);
            }
          }}
        >
          <div
            className="confirmModal"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 className="modalTitle">Ready to Log In?</h2>

            <p className="modalText">
              Check that this is the pattern you want to use.
            </p>

            <p className="modalReminder">Your chosen order is:</p>

            <div className="modalSequence">
              {selectedCards.map((card, index) => (
                <div key={card.name} className="modalCard">
                  <div className="modalBadge">{index + 1}</div>
                  <img src={card.src} alt={card.name} />
                  <p>{card.name}</p>
                </div>
              ))}
            </div>

            <div className="modalButtons">
              <button
                className="modalBtn modalBtnSecondary"
                onClick={() => setShowConfirmModal(false)}
                type="button"
                disabled={isSubmitting}
              >
                Go Back
              </button>

              <button
                className="modalBtn modalBtnPrimary"
                onClick={handleLogin}
                type="button"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Logging in..." : "Yes, Log Me In"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}